import React from 'react';
import { Modal, Button, Card, Badge, Alert } from 'react-bootstrap';
import { Save } from 'lucide-react';
import type { SkinSeoulAdminProduct } from '../../../../models/ProductTypes';
import { detectProductChanges } from '../utils/changeDetection';
import { ChangeItem } from './ChangeItem';

interface SaveConfirmationModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: () => void;
  originalProduct: SkinSeoulAdminProduct | null;
  productsToEdit: SkinSeoulAdminProduct[];
}

export const SaveConfirmationModal: React.FC<SaveConfirmationModalProps> = ({
  show,
  onHide,
  onConfirm,
  originalProduct,
  productsToEdit,
}) => {
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton style={{ backgroundColor: '#fff3cd', borderBottom: '2px solid #ffc107' }}>
        <Modal.Title style={{ color: '#664d03', fontWeight: '700' }}>⚠️ Confirm Changes</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          <strong>You are about to save changes to {productsToEdit.length} SKUs:</strong>
        </p>

        {productsToEdit.map((product, index) => (
          <Card
            key={product.productId}
            className={`mb-2 ${index > 0 ? 'ms-4' : ''}`}
            style={{
              borderLeft: index > 0 ? '3px solid #6c757d' : 'none',
              backgroundColor: index > 0 ? '#f8f9fa' : 'white',
            }}
          >
            <Card.Body className="p-3">
              <div className="d-flex align-items-start mb-2">
                <Badge bg={index === 0 ? 'primary' : 'secondary'} className="me-2">
                  {index === 0 ? 'Variable' : `Variation ${index}`}
                </Badge>
                <div className="flex-grow-1">
                  <div className="fw-bold mb-1">{index === 0 ? product.name : `└─ ${product.name}`}</div>
                  <small className="text-muted">SKU: {product.sku}</small>
                </div>
              </div>

              {/* Show changes - for main product and variations */}
              {originalProduct &&
                (() => {
                  // Get the original version of this product/variation
                  const originalVersion = index === 0 ? originalProduct : originalProduct.variations?.[index - 1];

                  if (!originalVersion) {
                    return null;
                  }

                  // Use shared utility to detect changes
                  const changes = detectProductChanges(product, originalVersion);

                  if (!changes) {
                    return null;
                  }

                  return (
                    <Alert variant="warning" className="mt-2 mb-0">
                      <h6 className="mb-2" style={{ fontSize: '0.95rem', fontWeight: '700', color: '#664d03' }}>
                        ⚠️ Changes to be saved:
                      </h6>
                      <ul className="list-unstyled mb-0" style={{ fontSize: '0.9rem', color: '#664d03' }}>
                        {changes.status && (
                          <ChangeItem
                            label="Stock Status"
                            oldValue={changes.status.old}
                            newValue={changes.status.new}
                          />
                        )}
                        {changes.postStatus && (
                          <ChangeItem
                            label="Publication Status"
                            oldValue={changes.postStatus.old}
                            newValue={changes.postStatus.new}
                          />
                        )}
                        {changes.name && (
                          <ChangeItem label="Name" oldValue={changes.name.old} newValue={changes.name.new} />
                        )}
                        {changes.categories && (
                          <ChangeItem
                            label="Category"
                            oldValue={changes.categories.old.join(' › ')}
                            newValue={changes.categories.new.join(' › ')}
                          />
                        )}
                        {changes.brand && (
                          <ChangeItem label="Brand" oldValue={changes.brand.old} newValue={changes.brand.new} />
                        )}
                        {changes.size && (
                          <ChangeItem label="Size" oldValue={changes.size.old} newValue={changes.size.new} />
                        )}
                        {changes.color && (
                          <ChangeItem label="Color" oldValue={changes.color.old} newValue={changes.color.new} />
                        )}
                        {changes.pack && (
                          <ChangeItem label="Pack" oldValue={changes.pack.old} newValue={changes.pack.new} />
                        )}
                        {changes.attributes?.['ss_name_kor'] && (
                          <ChangeItem
                            label="SS Name Kor"
                            oldValue={changes.attributes['ss_name_kor'].old}
                            newValue={changes.attributes['ss_name_kor'].new}
                          />
                        )}
                        {changes.attributes?.['cp_url'] && (
                          <ChangeItem
                            label="CP URL"
                            oldValue={changes.attributes['cp_url'].old}
                            newValue={changes.attributes['cp_url'].new}
                            wordBreak
                          />
                        )}
                      </ul>
                    </Alert>
                  );
                })()}
            </Card.Body>
          </Card>
        ))}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onConfirm} size="lg">
          <Save size={18} className="me-2" />
          Confirm & Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
